import React, { useEffect, useMemo } from 'react';
import { useAnimation } from '../hooks/useAnimation';
import BigBtn from '../design/components/BigBtn';
import ThemePicker from '../design/components/ThemePicker';
import { playerStyleFor } from '../design/themes';

const PODIUM_HEIGHTS = [110, 84, 60]; // 1st, 2nd, 3rd
const PODIUM_TINTS = ['var(--c-yellow)', 'var(--c-pink)', 'var(--c-blue)'];
const PODIUM_LABELS = ['1ST', '2ND', '3RD'];

const GameFinishedScreen = ({ data, onPlayAgain, onExitToHome }) => {
  const { createConfetti } = useAnimation();

  const sortedScores = useMemo(() => {
    if (!data?.finalScores) return [];
    return [...data.finalScores].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }, [data]);

  const winner = sortedScores[0];
  const topThree = sortedScores.slice(0, 3);
  const rest = sortedScores.slice(3);
  // Display order for podium: 2nd, 1st, 3rd
  const podiumOrder = [topThree[1], topThree[0], topThree[2]].map((s, i) => ({ s, rank: [1, 0, 2][i] }));

  useEffect(() => {
    if (!data) return;
    createConfetti();
    const t1 = setTimeout(() => createConfetti(), 1000);
    const t2 = setTimeout(() => createConfetti(), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [data, createConfetti]);

  if (!data) return null;

  return (
    <div className="pen-screen" style={{ paddingBottom: 24 }}>
      {/* TopBar-lite */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px 6px', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--c-yellow)',
            border: 'var(--border-ink)',
            boxShadow: 'var(--shadow-hard-sm)',
            display: 'grid', placeItems: 'center',
            fontSize: 18,
          }}>🏆</div>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
            letterSpacing: 0.8, color: 'var(--c-ink)',
          }}>TOURNAMENT · COMPLETE</div>
        </div>
        <ThemePicker />
      </div>

      {/* Champion banner */}
      <div style={{ padding: '8px 16px 4px' }}>
        <div style={{
          position: 'relative',
          background: 'var(--c-yellow)',
          border: 'var(--border-ink)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-hard)',
          padding: '14px 16px',
          overflow: 'hidden',
          animation: 'penBounceIn 520ms var(--ease-pop) both',
        }}>
          <div style={{
            position: 'absolute', top: -10, right: -10,
            width: 80, height: 80,
            background: 'var(--c-pink)',
            opacity: 0.3,
            borderRadius: '50%',
          }} />
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1.2,
            color: 'var(--c-ink)', opacity: 0.8,
          }}>★ CHAMPION ★</div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 28, lineHeight: 1,
            marginTop: 6, color: 'var(--c-ink)',
            textTransform: 'uppercase', letterSpacing: -0.5,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{(winner?.id || 'NOBODY').toString().toUpperCase()}</div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginTop: 10,
          }}>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
              padding: '4px 8px',
              background: 'var(--c-ink)', color: 'var(--c-yellow)',
              borderRadius: 6, letterSpacing: 0.6,
            }}>+{winner?.score ?? 0} {(winner?.score ?? 0) === 1 ? 'WIN' : 'WINS'}</div>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 10,
              color: 'var(--c-ink)', opacity: 0.75,
            }}>OUT OF {sortedScores.length} PLAYERS</div>
          </div>
        </div>
      </div>

      {/* Podium */}
      {topThree.length > 0 && (
        <div style={{ padding: '18px 16px 6px' }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.8,
            color: 'var(--c-ink)', marginBottom: 10,
          }}>PODIUM</div>
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            gap: 10, minHeight: 150,
          }}>
            {podiumOrder.filter(({ s }) => !!s).map(({ s, rank }, i) => {
              const palette = playerStyleFor(rank);
              return (
                <div key={s.id} style={{
                  flex: 1, maxWidth: 110,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  animation: `penBounceIn 480ms ${300 + i * 140}ms var(--ease-pop) both`,
                }}>
                  {/* avatar */}
                  <div style={{
                    width: 44, height: 44, borderRadius: 999,
                    background: palette.color,
                    border: 'var(--border-ink)',
                    boxShadow: 'var(--shadow-hard-sm)',
                    display: 'grid', placeItems: 'center',
                    fontSize: 22,
                  }}>{palette.emoji}</div>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.4,
                    color: 'var(--c-ink)',
                    maxWidth: '100%',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{(s.id || '—').toString().toUpperCase()}</div>
                  {/* block */}
                  <div style={{
                    width: '100%',
                    height: PODIUM_HEIGHTS[rank],
                    background: PODIUM_TINTS[rank],
                    border: 'var(--border-ink)',
                    borderRadius: '10px 10px 0 0',
                    boxShadow: 'var(--shadow-hard)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                    paddingTop: 8, gap: 4,
                  }}>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontSize: 18,
                      color: 'var(--c-ink)',
                    }}>{PODIUM_LABELS[rank]}</div>
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
                      padding: '2px 6px',
                      background: 'var(--c-ink)', color: 'var(--c-yellow)',
                      borderRadius: 4,
                    }}>{s.score ?? 0}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Rest of leaderboard */}
      {rest.length > 0 && (
        <div style={{ padding: '14px 16px 0' }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.8,
            color: 'var(--c-ink)', marginBottom: 8,
          }}>ALSO RAN</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rest.map((s, i) => {
              const rank = i + 4;
              const palette = playerStyleFor(rank - 1);
              return (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px',
                  background: 'var(--c-cream)',
                  border: 'var(--border-ink-sm)',
                  borderRadius: 'var(--radius-chip)',
                  animation: `penBounceIn 360ms ${600 + i * 80}ms var(--ease-pop) both`,
                }}>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 13,
                    background: 'var(--c-ink)', color: 'var(--c-cream)',
                    padding: '4px 8px', borderRadius: 6,
                    minWidth: 28, textAlign: 'center',
                  }}>#{rank}</div>
                  <div style={{
                    width: 22, height: 22, borderRadius: 999,
                    background: palette.color, border: 'var(--border-ink-sm)',
                    display: 'grid', placeItems: 'center', fontSize: 11,
                  }}>{palette.emoji}</div>
                  <div style={{
                    flex: 1, fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: 0.4,
                    color: 'var(--c-ink)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{(s.id || '—').toString().toUpperCase()}</div>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13,
                    padding: '3px 7px', borderRadius: 5,
                    background: 'var(--c-ink)', color: 'var(--c-yellow)',
                    minWidth: 36, textAlign: 'right',
                  }}>{s.score ?? 0}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA */}
      <div style={{ padding: '18px 16px 8px', display: 'flex', gap: 8 }}>
        <BigBtn onClick={onPlayAgain} primary>🔁 PLAY AGAIN →</BigBtn>
        <BigBtn onClick={onExitToHome}>🏠 EXIT TO HOME</BigBtn>
      </div>
    </div>
  );
};

export default GameFinishedScreen;
